import React, { useMemo, useState } from 'react';
import { Sparkles, Check, Link2, Plus, Coins, AlertCircle } from 'lucide-react';
import type { AIProposal } from '../../services/aiProxy';
import {
  applyProposal,
  selectAll,
  type QuestApplySelection,
} from '../../services/aiProposalApply';
import { useWalletStore } from '../../stores/walletStore';

// Phase 2 proposal card (docs/AI_PHASE2_PLAN.md): renders a write-tool
// proposal as a selectable tree — the quest with its goals and their linked
// actions — with per-item checkboxes, "Apply selected" and "Apply all".
// Applying charges spend_coin(create_quest) FIRST (free while the price key
// is draft); on insufficient Coin nothing is created.

interface ProposalPreviewProps {
  proposal: AIProposal;
}

export function ProposalPreview({ proposal }: ProposalPreviewProps) {
  const input = proposal.input ?? {};
  const goals: any[] = Array.isArray(input.goals) ? input.goals : [];

  const [selection, setSelection] = useState<QuestApplySelection>(() => selectAll(input));
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const priceCp = useWalletStore((s) => s.priceFor('create_quest'));

  const selectedCount = useMemo(() => selection.goals.filter(Boolean).length, [selection]);

  if (proposal.capability !== 'create_quest') return null;

  const toggleGoal = (i: number) =>
    setSelection((s) => ({ ...s, goals: s.goals.map((v, j) => (j === i ? !v : v)) }));
  const toggleLinked = (i: number) =>
    setSelection((s) => ({ ...s, linkedActions: s.linkedActions.map((v, j) => (j === i ? !v : v)) }));

  const run = async (sel: QuestApplySelection) => {
    setBusy(true);
    setError(null);
    try {
      const result = await applyProposal(proposal, sel);
      if (result.status === 'applied') {
        const o = result.outcome;
        const bits = [`${o.goalsCreated} goal${o.goalsCreated === 1 ? '' : 's'}`];
        if (o.newHabits) bits.push(`${o.newHabits} new habit${o.newHabits === 1 ? '' : 's'}`);
        if (o.newTasks) bits.push(`${o.newTasks} new task${o.newTasks === 1 ? '' : 's'}`);
        if (o.existingLinks) bits.push(`${o.existingLinks} linked`);
        setApplied(
          `Quest created — ${bits.join(', ')}` +
            (result.spend.priceCp > 0 ? ` · −${result.spend.priceCp} cp` : ''),
        );
      } else if (result.status === 'insufficient_coin') {
        setError(`Not enough Coin — this costs ${result.spend.priceCp} cp, you have ${result.spend.balanceCp} cp. Top up in Status → Wallet.`);
      } else if (result.status === 'cancelled') {
        setError('Cancelled — nothing was created and nothing was charged.');
      } else if (result.status === 'capacity_blocked') {
        // ⚠️ SILENT HERE ON PURPOSE — `CapacityBlockModal` is already open with
        // the allowance and its remedies. ⚠️ It must NOT fall through to the
        // `else` below, which would blame the proposal for missing fields —
        // wrong, and unactionable.
      } else {
        setError('This proposal is missing required fields and cannot be applied.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Apply failed.');
    } finally {
      setBusy(false);
    }
  };

  const linkedLabel = (la: any): string => {
    if (!la) return '';
    if (la.kind === 'new_habit') return `new habit: ${la.name}${la.interval === 'weekly' ? ' (weekly)' : ' (daily)'}`;
    if (la.kind === 'new_task') return `new task: ${la.name}`;
    if (la.kind === 'existing_habit') return 'link existing habit';
    if (la.kind === 'existing_task') return 'link existing task';
    return '';
  };

  if (applied) {
    return (
      <div className="max-w-[85%] rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>{applied}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[85%] rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-sm font-semibold">Proposed Quest: {input.title}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">
          {input.category}
        </span>
      </div>
      {input.description && (
        <p className="text-xs text-muted-foreground">{input.description}</p>
      )}

      <ul className="space-y-1.5">
        {goals.map((g, i) => (
          <li key={i} className="text-sm">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 accent-current"
                checked={selection.goals[i] ?? false}
                onChange={() => toggleGoal(i)}
                disabled={busy}
              />
              <span className={selection.goals[i] ? '' : 'opacity-50 line-through'}>
                {g.actionVerb} {g.what} ×{g.quantity}
                {g.byWhen ? <span className="text-muted-foreground"> · by {g.byWhen}</span> : null}
              </span>
            </label>
            {g.linkedAction && (
              <label className="ml-6 mt-0.5 flex items-start gap-2 cursor-pointer text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-current"
                  checked={(selection.goals[i] && selection.linkedActions[i]) ?? false}
                  onChange={() => toggleLinked(i)}
                  disabled={busy || !selection.goals[i]}
                />
                <span className={selection.goals[i] && selection.linkedActions[i] ? '' : 'opacity-50'}>
                  {g.linkedAction.kind?.startsWith('existing') ? (
                    <Link2 className="w-3 h-3 inline mr-1" />
                  ) : (
                    <Plus className="w-3 h-3 inline mr-1" />
                  )}
                  {linkedLabel(g.linkedAction)}
                </span>
              </label>
            )}
          </li>
        ))}
      </ul>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-500">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => void run(selectAll(input))}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
            bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Check className="w-3 h-3" />
          {busy ? 'Applying…' : 'Apply all'}
        </button>
        <button
          onClick={() => void run(selection)}
          disabled={busy || selectedCount === 0 || selectedCount === goals.length}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/10 text-primary
            hover:bg-primary/20 transition-colors disabled:opacity-40"
          title="Apply only the checked goals/links"
        >
          Apply selected ({selectedCount})
        </button>
        {typeof priceCp === 'number' && priceCp > 0 && (
          <span className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
            <Coins className="w-3 h-3" />
            {priceCp} cp
          </span>
        )}
      </div>
    </div>
  );
}
